import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, FlaskConical, Layers, Ruler, Palette } from "lucide-react";
import { MaskLine, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { getProducts, getCategories } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

export default function Home() {
  const { t, lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [parallax, setParallax] = useState(false);
  const heroRef = useRef(null);

  const MANIFESTO = [
    { n: "01", title: t("home.m1Title"), body: t("home.m1Body") },
    { n: "02", title: t("home.m2Title"), body: t("home.m2Body") },
    { n: "03", title: t("home.m3Title"), body: t("home.m3Body") },
  ];

  const STATS = [
    { value: "2020", label: t("home.founded") },
    { value: "12+", label: t("home.products") },
    { value: "ISO", label: "9001:2015" },
    { value: "Premium", label: t("home.rawMaterials") },
  ];

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "12%"]);

  useEffect(() => {
    getProducts().then((d) => setProducts(d.slice(0, 4))).catch(() => {});
    getCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setParallax(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.ashlarbuildchem.com/#organization",
        "name": "Ashlar Buildchem",
        "url": "https://www.ashlarbuildchem.com",
        "logo": "https://www.ashlarbuildchem.com/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-1800-309-0808",
          "contactType": "customer service",
          "email": "care@ashlarbuildchem.com",
          "areaServed": "IN"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "247(NE), Scheme 4C, Jamunapuri",
          "addressLocality": "Jaipur",
          "addressRegion": "Rajasthan",
          "postalCode": "302039",
          "addressCountry": "IN"
        },
        "sameAs": [
          "https://www.instagram.com/ashlarbuildchem",
          "https://www.linkedin.com/company/ashlarbuildchem",
          "https://www.youtube.com/@ashlarbuildchem"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://www.ashlarbuildchem.com/#website",
        "url": "https://www.ashlarbuildchem.com",
        "name": "Ashlar Buildchem",
        "publisher": {
          "@id": "https://www.ashlarbuildchem.com/#organization"
        }
      }
    ]
  };

  return (
    <div data-testid="home-page">
      <SEO {...SEO_CONFIG.home} schema={homeSchema} />
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-6 pb-16 pt-32 md:px-10 md:pb-24 md:pt-40">
        <div className="mx-auto grid max-w-[1600px] items-center gap-12 lg:grid-cols-[1.15fr_1fr]">
          {/* Left: copy */}
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="mb-6 flex items-center gap-3"
            >
              <span className="h-2 w-2 rotate-45 bg-primary" />
              <span className="label text-muted-foreground">{t("home.eyebrow")}</span>
            </motion.div>

            <h1
              className={`font-display text-[9vw] font-extrabold text-foreground sm:text-[10vw] md:text-[6.6vw] lg:text-[5.4vw] ${
                lang === "hi"
                  ? "normal-case leading-[1.15] tracking-normal"
                  : "uppercase leading-[0.9] tracking-tight"
              }`}
            >
              <MaskLine delay={0.1}>{t("home.heroLine1")}</MaskLine>
              <MaskLine delay={0.24}>
                <span className="text-primary">{t("home.heroLine2a")}</span>{t("home.heroLine2b")}
              </MaskLine>
              <MaskLine delay={0.38}>{t("home.heroLine3")}</MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg"
            >
              {t("home.heroBody")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link
                to="/products"
                data-testid="hero-explore-products"
                className="group inline-flex items-center gap-3 bg-primary px-8 py-4 text-primary-foreground transition-colors duration-200 hover:bg-foreground"
              >
                <span className="label text-[0.7rem]">{t("home.exploreProducts")}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
              </Link>
              <Link
                to="/calculators"
                data-testid="hero-calculators"
                className="group inline-flex items-center gap-3 border border-black/15 px-8 py-4 text-foreground transition-colors duration-200 hover:bg-secondary"
              >
                <span className="label text-[0.7rem]">{t("home.coverageCalculators")}</span>
                <Ruler className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          {/* Right: framed parallax image */}
          <div className="relative">
            <div className="clip-corner-lg overflow-hidden border border-black/10">
              <motion.img
                style={parallax ? { y: imgY } : undefined}
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1600&auto=format&fit=crop"
                alt={t("home.architectureAlt")}
                className="block h-[340px] w-full scale-110 object-cover md:h-[560px]"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 hidden h-24 w-24 bg-primary md:block" />
            <div className="absolute -right-3 -top-3 hidden h-16 w-16 border-2 border-primary md:block" />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-black/10 bg-background py-8" data-testid="home-marquee">
        <Marquee
          items={["Ashlar Buildchem", "ISO 9001:2015", "ISI Marked", t("home.marqueeAdvanced"), t("home.marqueeCertified")]}
          duration={38}
        />
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40" data-testid="home-manifesto">
        <Reveal>
          <span className="label text-primary">{t("home.manifesto")}</span>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
            {t("home.manifestoTitle")}
          </h2>
        </Reveal>

        <div className="mt-20 grid gap-px bg-black/10 md:grid-cols-3">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12} className="bg-background">
              <div className="group relative h-full p-8 md:p-10">
                <span className="font-mono text-6xl font-bold text-black/10 transition-colors duration-300 group-hover:text-primary/40 md:text-7xl">
                  {m.n}
                </span>
                <h3 className="mt-6 font-display text-2xl font-semibold">{m.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/10 bg-secondary/40" data-testid="home-stats">
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-px bg-black/10 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="min-w-0 bg-background">
              <div className="p-5 sm:p-8 lg:p-12">
                <div className="whitespace-nowrap font-display text-xl font-bold text-primary sm:text-4xl lg:text-5xl 2xl:text-6xl">{s.value}</div>
                <div className="label mt-3 text-muted-foreground">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40" data-testid="home-categories">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="label text-primary">{t("home.catalogue")}</span>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
              {t("home.systemTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/products" className="group inline-flex items-center gap-2 text-foreground" data-testid="home-view-all-cats">
              <span className="label">{t("home.allProducts")}</span>
              <ArrowRight className="h-4 w-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-px bg-black/10 md:grid-cols-6">
          {cats.map((c, i) => (
            <Reveal
              key={c.slug}
              delay={i * 0.06}
              className={`bg-background ${i === 0 ? "md:col-span-4" : "md:col-span-2"} ${i === 3 ? "md:col-span-4" : ""} ${i === 4 ? "md:col-span-2" : ""} ${i === 1 || i === 2 ? "md:col-span-3" : ""}`}
            >
              <Link
                to={`/products?cat=${c.slug}`}
                data-testid={`home-cat-${c.slug}`}
                className="group relative flex h-full min-h-[280px] flex-col justify-end overflow-hidden p-8 md:p-10"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="absolute inset-0 h-full w-full object-cover opacity-30 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-45 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                <div className="relative">
                  <span className="label text-primary">0{i + 1} · {c.code}</span>
                  <h3 className="mt-3 font-display text-3xl font-semibold md:text-4xl">{c.name}</h3>
                  <p className="mt-2 max-w-md text-sm text-muted-foreground">{c.tagline}</p>
                </div>
                <ArrowUpRight className="absolute right-8 top-8 h-6 w-6 text-foreground transition-all duration-300 group-hover:rotate-45 group-hover:text-primary" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CALCULATOR TEASER */}
      <section className="border-y border-black/10 bg-secondary/40" data-testid="home-calc-teaser">
        <div className="mx-auto grid max-w-[1600px] gap-px bg-black/10 md:grid-cols-3">
          {[
            { icon: Layers, title: t("home.adhesiveCoverage"), body: t("home.adhesiveBody"), tool: "adhesive" },
            { icon: Ruler, title: t("home.groutCoverage"), body: t("home.groutBody"), tool: "grout" },
            { icon: Palette, title: t("home.groutVisualiser"), body: t("home.visualiserBody"), tool: "visualizer" },
          ].map((c, i) => (
            <Reveal key={c.tool} delay={i * 0.1} className="bg-background">
              <Link
                to={`/calculators?tool=${c.tool}`}
                data-testid={`home-calc-${c.tool}`}
                className="group flex h-full flex-col justify-between gap-10 p-10 transition-colors duration-300 hover:bg-secondary md:p-16"
              >
                <c.icon className="h-10 w-10 text-primary" strokeWidth={1.4} />
                <div>
                  <span className="label text-muted-foreground">{t("home.estimator")} {i + 1}</span>
                  <h3 className="mt-4 font-display text-3xl font-semibold md:text-5xl">{c.title}</h3>
                  <p className="mt-4 max-w-md text-base text-muted-foreground">{c.body}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-foreground">
                    <span className="label">{t("home.openCalculator")}</span>
                    <ArrowUpRight className="h-4 w-4 text-primary transition-transform duration-200 group-hover:rotate-45" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-40" data-testid="home-featured">
        <Reveal>
          <span className="label text-primary">{t("home.signature")}</span>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
            {t("home.builtToBond")}
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-black/10 bg-primary text-primary-foreground" data-testid="home-cta">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-10 px-6 py-24 md:flex-row md:items-center md:justify-between md:px-10 md:py-32">
          <div className="flex items-center gap-6">
            <ShieldCheck className="hidden h-16 w-16 shrink-0 md:block" strokeWidth={1.2} />
            <h2 className="font-display text-4xl font-bold leading-[0.95] tracking-tight md:text-7xl">
              {t("home.readyTitle")}
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="home-cta-contact"
            className="group inline-flex items-center gap-3 bg-background px-10 py-6 text-foreground transition-transform duration-200 hover:-translate-y-1"
          >
            <span className="label">{t("home.talkTeam")}</span>
            <ArrowUpRight className="h-5 w-5 text-primary transition-transform duration-200 group-hover:rotate-45" />
          </Link>
        </div>
        <FlaskConical className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 text-primary-foreground/10" strokeWidth={0.8} />
      </section>
    </div>
  );
}
